import { PlusCircle, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import loggingService from '../../services/loggingService';
import { invoiceApi } from '../Invoice/invoiceApi';
import { customerApi } from '../Onboarding/CustomerOnboarding/customerApi';
import { getAllSites } from '../SiteManagement/siteApi';
import Input from '../ui/input.js';
import { facilityBookingApi } from './facilityBookingApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

// Calculate expiry date (30 days from a given date)
const getExpiryDate = (fromDate) => {
  const date = new Date(fromDate || getTodayDate());
  date.setDate(date.getDate() + 30);
  return date.toISOString().split('T')[0];
};

const createEmptyLineItem = () => ({
  name: '',
  units: '',
  rate: '',
  amount: '',
});

const uniqueId = () => Math.random().toString(36).slice(2, 10);

const createTaxRow = () => ({
  id: uniqueId(),
  label: '',
  applyOn: 'subtotalAfterDiscount',
  percentage: '',
});

const numberOrZero = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveSiteValue = (site) => {
  const value =
    site?.site_id ??
    site?.siteId ??
    site?.id ??
    site?._id ??
    site?.site_code ??
    site?.siteCode;
  return value !== undefined && value !== null ? String(value) : '';
};

const resolveSiteLabel = (site) =>
  site?.site_name ||
  site?.name ||
  site?.siteCode ||
  site?.site_code ||
  `Site ${site?.site_id ?? site?.id ?? ''}`;

// Helper function to capitalize first letter
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Cache key for localStorage
const CACHE_KEY = 'facilityBookingInvoice_cache';

const FacilityBookingInvoice = () => {
  const { openOverlay } = useOverlay();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get booking data from navigation state
  const bookingData = location.state?.bookingData || null;
  
  // Get invoice data from bill (if coming from FacilityBookingBillCustomer)
  const fromBill = location.state?.fromBill || false;
  const invoiceData = location.state?.invoiceData || null;

  // Debug: Log received data
  console.log('FacilityBookingInvoice - Received booking data:', bookingData);
  console.log('FacilityBookingInvoice - Received invoice data:', invoiceData);
  console.log('FacilityBookingInvoice - Location state:', location.state);

  // Store the initial site ID from invoice data to set after sites are loaded
  const initialSiteId = invoiceData?.siteId || '';

  // Check if we have fresh data from navigation (booking or bill)
  const hasFreshData = !!(bookingData || invoiceData);

  // Cache key for booking data specifically
  const BOOKING_DATA_CACHE_KEY = 'facilityBookingInvoice_bookingData';

  // Load cached data from localStorage
  const loadCachedData = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading cached invoice data:', error);
    }
    return null;
  };

  // Load cached booking data
  const loadCachedBookingData = () => {
    try {
      const cached = localStorage.getItem(BOOKING_DATA_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading cached booking data:', error);
    }
    return null;
  };

  // Save booking data to localStorage
  const saveBookingDataToCache = (data) => {
    try {
      if (data) {
        localStorage.setItem(BOOKING_DATA_CACHE_KEY, JSON.stringify(data));
      }
    } catch (error) {
      console.error('Error saving booking data to cache:', error);
    }
  };

  // Save data to localStorage
  const saveCacheData = (data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving invoice data to cache:', error);
    }
  };

  // Clear cache from localStorage
  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(BOOKING_DATA_CACHE_KEY);
    } catch (error) {
      console.error('Error clearing invoice cache:', error);
    }
  };

  // Save booking data to cache if we have fresh data from navigation
  if (bookingData) {
    saveBookingDataToCache(bookingData);
  }

  // Get effective booking data - from navigation state or cache
  const effectiveBookingData = bookingData || loadCachedBookingData();
  const effectiveHasFreshData = !!(effectiveBookingData || invoiceData);

  // Get initial data - prefer navigation state, then cache
  const cachedData = !effectiveHasFreshData ? loadCachedData() : null;
  
  const [formData, setFormData] = useState(() => {
    // If we have fresh data from navigation or cached booking data, use it
    if (effectiveHasFreshData) {
      return {
        siteSelection: '',
        clientSelection: invoiceData?.customerId || '',
        date: invoiceData?.date || effectiveBookingData?.startDate || getTodayDate(),
        paymentMode: '',
        estimatedShipDate: invoiceData?.estimatedShipDate || effectiveBookingData?.startDate || '',
        dateOfExpiry: invoiceData?.dateOfExpiry || effectiveBookingData?.endDate || getExpiryDate(effectiveBookingData?.startDate),
        shippingAddress: invoiceData?.shippingAddress || 
          (effectiveBookingData ? [effectiveBookingData.blockNumber, effectiveBookingData.apartmentFlatNo].filter(Boolean).join(', ') : ''),
        specialNotes: '',
        billingAddress: '',
        billingContact: invoiceData?.customerDetails?.name || invoiceData?.bookingDetails?.residentName || effectiveBookingData?.residentName || '',
        billingEmail: invoiceData?.customerDetails?.email || invoiceData?.bookingDetails?.email || effectiveBookingData?.email || '',
        billingPhone: invoiceData?.customerDetails?.mobile || invoiceData?.bookingDetails?.contactNumber || effectiveBookingData?.contactNumber || '',
        paymentReference: '',
        useBillingForShipping: false,
        facilityType: invoiceData?.bookingDetails?.facilityType || effectiveBookingData?.facilityType || '',
        bookingId: invoiceData?.bookingDetails?.bookingId || effectiveBookingData?.bookingId || '',
        billNumber: invoiceData?.billNumber || '',
        billId: invoiceData?.billId || '',
        // POC for Delivery fields
        pocName: invoiceData?.customerDetails?.name || invoiceData?.bookingDetails?.residentName || effectiveBookingData?.residentName || '',
        pocRole: capitalizeFirst(
          invoiceData?.bookingDetails?.whosBooking || 
          effectiveBookingData?.whosBooking || 
          bookingData?.whosBooking || 
          ''
        ),
        pocPhone: invoiceData?.customerDetails?.mobile || invoiceData?.bookingDetails?.contactNumber || effectiveBookingData?.contactNumber || '',
        pocEmail: invoiceData?.customerDetails?.email || invoiceData?.bookingDetails?.email || effectiveBookingData?.email || '',
        // Booking details fields
        purposeOfBooking: invoiceData?.bookingDetails?.purposeOfBooking || effectiveBookingData?.purposeOfBooking || '',
        numberOfPeople: invoiceData?.bookingDetails?.numberOfPeople || effectiveBookingData?.numberOfPeople || '',
        bookingNotes: invoiceData?.bookingDetails?.notes || effectiveBookingData?.notes || '',
        startTime: invoiceData?.bookingDetails?.startTime || effectiveBookingData?.startTime || '',
        endTime: invoiceData?.bookingDetails?.endTime || effectiveBookingData?.endTime || '',
      };
    }
    // Otherwise, use cached data if available
    if (cachedData?.formData) {
      return cachedData.formData;
    }
    // Default empty state
    return {
      siteSelection: '',
      clientSelection: '',
      date: getTodayDate(),
      paymentMode: '',
      estimatedShipDate: '',
      dateOfExpiry: getExpiryDate(),
      shippingAddress: '',
      specialNotes: '',
      billingAddress: '',
      billingContact: '',
      billingEmail: '',
      billingPhone: '',
      paymentReference: '',
      useBillingForShipping: false,
      facilityType: '',
      bookingId: '',
      billNumber: '',
      billId: '',
      // POC for Delivery fields
      pocName: '',
      pocRole: '',
      pocPhone: '',
      pocEmail: '',
      // Booking details fields
      purposeOfBooking: '',
      numberOfPeople: '',
      bookingNotes: '',
      startTime: '',
      endTime: '',
    };
  });

  // Calculate number of days between two dates
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };

  // Get number of days from booking data
  const bookingDays = effectiveBookingData ? calculateDays(effectiveBookingData.startDate, effectiveBookingData.endDate) : 1;

  // Initialize line items with invoice data (from bill), booking info, or cache
  const [lineItems, setLineItems] = useState(() => {
    // If we have fresh data from navigation or cached booking data
    if (effectiveHasFreshData) {
      if (invoiceData?.items && invoiceData.items.length > 0) {
        return invoiceData.items.map(item => ({
          name: item.name || '',
          units: item.units || item.quantity || '',
          rate: item.rate || '',
          amount: item.amount || '',
        }));
      }
      if (effectiveBookingData) {
        return [
          {
            name: `${effectiveBookingData.facilityType || 'Facility'} Booking`,
            units: String(bookingDays),
            rate: '',
            amount: '',
          },
        ];
      }
    }
    // Use cached data if available
    if (cachedData?.lineItems && cachedData.lineItems.length > 0) {
      return cachedData.lineItems;
    }
    return [createEmptyLineItem()];
  });
  const [discount, setDiscount] = useState(() => {
    if (cachedData?.discount) {
      return cachedData.discount;
    }
    return {
      type: 'percentage',
      value: '',
      shippingHandling: '',
      insurance: '',
    };
  });
  const [taxRows, setTaxRows] = useState(() => {
    // If we have fresh data from bill with tax rates, populate them
    if (effectiveHasFreshData && invoiceData) {
      const cgstRate = invoiceData.cgstRate;
      const sgstRate = invoiceData.sgstRate;
      
      if (cgstRate || sgstRate) {
        const rows = [];
        if (cgstRate) {
          rows.push({
            id: `tax-${Date.now()}-cgst`,
            label: 'CGST',
            percentage: String(cgstRate),
            applyOn: 'subtotal',
          });
        }
        if (sgstRate) {
          rows.push({
            id: `tax-${Date.now()}-sgst`,
            label: 'SGST',
            percentage: String(sgstRate),
            applyOn: 'subtotal',
          });
        }
        if (rows.length > 0) {
          return rows;
        }
      }
    }
    // Use cached data if available
    if (cachedData?.taxRows && cachedData.taxRows.length > 0) {
      return cachedData.taxRows;
    }
    return [createTaxRow()];
  });
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState({ success: '', error: '' });

  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      // Calculate amount based on units (Days) and rate
      if (field === 'units' || field === 'rate') {
        const units =
          field === 'units' ? numberOrZero(value) : numberOrZero(updated.units);
        const rate = field === 'rate' ? numberOrZero(value) : numberOrZero(updated.rate);
        const amount = units * rate;
        updated.amount = amount > 0 ? amount.toFixed(2) : '';
      }
      next[index] = updated;
      return next;
    });
  };

  const handleAddLineItem = () => {
    setLineItems((prev) => [...prev, createEmptyLineItem()]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleDiscountChange = (field, value) => {
    setDiscount((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaxRowChange = (id, field, value) => {
    setTaxRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddTaxRow = () => {
    setTaxRows((prev) => [...prev, createTaxRow()]);
  };

  const handleRemoveTaxRow = (id) => {
    setTaxRows((prev) => prev.filter((row) => row.id !== id));
  };

  const summary = useMemo(() => {
    const subtotal = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const discountValue = numberOrZero(discount.value);
    const computedDiscount =
      discount.type === 'percentage'
        ? subtotal * (discountValue / 100)
        : discountValue;
    const shippingHandling = numberOrZero(discount.shippingHandling);
    const insurance = numberOrZero(discount.insurance);
    const subtotalAfterDiscount = Math.max(subtotal - computedDiscount, 0);
    const netTotalBeforeTax = subtotalAfterDiscount + shippingHandling + insurance;

    const resolveBaseAmount = (applyOn) => {
      switch (applyOn) {
        case 'subtotal':
          return subtotal;
        case 'subtotalAfterDiscount':
          return subtotalAfterDiscount;
        case 'shippingHandling':
          return shippingHandling;
        case 'insurance':
          return insurance;
        case 'netTotalBeforeTax':
          return netTotalBeforeTax;
        default:
          return subtotalAfterDiscount;
      }
    };

    const taxes = taxRows.map((row) => {
      const base = resolveBaseAmount(row.applyOn);
      const percent = numberOrZero(row.percentage);
      const amount = base * (percent / 100);
      return { ...row, amount };
    });

    const totalTax = taxes.reduce((sum, row) => sum + row.amount, 0);
    const grandTotal = netTotalBeforeTax + totalTax;

    return {
      subtotal,
      computedDiscount,
      shippingHandling,
      insurance,
      subtotalAfterDiscount,
      netTotalBeforeTax,
      taxes,
      totalTax,
      grandTotal,
    };
  }, [discount, lineItems, taxRows]);

  // Save form data to cache whenever it changes
  useEffect(() => {
    // Don't save if we just navigated with fresh data (initial render)
    const cacheData = {
      formData,
      lineItems,
      discount,
      taxRows,
      timestamp: Date.now(),
    };
    saveCacheData(cacheData);
    
    // Also store rate and tax rates by booking ID or booking signature for bill to retrieve
    const bookingId = bookingData?.bookingId || effectiveBookingData?.bookingId;
    const currentBooking = bookingData || effectiveBookingData;
    
    // Create a unique key from booking details if bookingId is not available
    const getBookingKey = () => {
      if (bookingId) {
        return `facilityBookingBillData_${bookingId}`;
      }
      // Use booking signature: siteId_facilityType_startDate_endDate_residentName
      if (currentBooking) {
        const signature = [
          currentBooking.siteId || '',
          currentBooking.facilityType || '',
          currentBooking.startDate || '',
          currentBooking.endDate || '',
          currentBooking.residentName || '',
        ].join('_');
        return `facilityBookingBillData_${signature}`;
      }
      return null;
    };
    
    const getSignatureKey = () => {
      // Always generate signature key regardless of bookingId
      if (currentBooking) {
        const signature = [
          currentBooking.siteId || '',
          currentBooking.facilityType || '',
          currentBooking.startDate || '',
          currentBooking.endDate || '',
          currentBooking.residentName || '',
        ].join('_');
        return `facilityBookingBillData_${signature}`;
      }
      return null;
    };
    
    const billDataKey = getBookingKey();
    const signatureKey = getSignatureKey();
    
    if (lineItems.length > 0 && lineItems[0].rate) {
      try {
        const cgstRow = taxRows.find(row => row.label === 'CGST');
        const sgstRow = taxRows.find(row => row.label === 'SGST');
        
        const billData = {
          rate: lineItems[0].rate,
          cgstRate: cgstRow?.percentage || '',
          sgstRate: sgstRow?.percentage || '',
          timestamp: Date.now(),
          bookingId: bookingId || null, // Store bookingId if available
        };
        
        // Store with bookingId key if available
        if (billDataKey) {
          localStorage.setItem(billDataKey, JSON.stringify(billData));
          console.log('💾 Stored bill data with bookingId key:', billDataKey, billData);
        }
        
        // Also store with signature key (for backward compatibility and when bookingId is undefined)
        if (signatureKey && signatureKey !== billDataKey) {
          localStorage.setItem(signatureKey, JSON.stringify(billData));
          console.log('💾 Stored bill data with signature key:', signatureKey, billData);
        }
      } catch (error) {
        console.error('Error storing bill data:', error);
      }
    }
  }, [formData, lineItems, discount, taxRows, bookingData?.bookingId, effectiveBookingData?.bookingId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateView = (event) => setIsMobileView(event.matches);
    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
          
          // If coming from bill page with a site ID, set the site selection and billing address
          if (initialSiteId && sorted.length > 0) {
            const matchingSite = sorted.find(s => resolveSiteValue(s) === initialSiteId);
            if (matchingSite) {
              const siteValue = resolveSiteValue(matchingSite);
              setFormData(prev => ({
                ...prev,
                siteSelection: siteValue,
                billingAddress: siteValue,
                // Keep the shipping address from invoice data (customer address) - don't change it
              }));
            }
          }
          // Auto-select the first site if coming from facility booking and no site is selected
          else if (effectiveBookingData && sorted.length > 0 && !formData.siteSelection) {
            const firstSiteValue = resolveSiteValue(sorted[0]);
            if (firstSiteValue) {
              // Build customer address from booking data for shipping
              const customerShippingAddress = [
                effectiveBookingData.blockNumber,
                effectiveBookingData.apartmentFlatNo,
              ].filter(Boolean).join(', ');
              
              setFormData(prev => ({
                ...prev,
                siteSelection: firstSiteValue,
                billingAddress: firstSiteValue,
                // Shipping address should be customer's address, not site
                shippingAddress: prev.shippingAddress || customerShippingAddress || '',
              }));
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(
            error?.message || 'Unable to load sites. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      setIsCustomersLoading(true);
      setCustomersError('');
      try {
        const result = await customerApi.listCustomers({ limit: 100, status: 'active' });
        if (result.success) {
          const sorted = [...(result.customers || [])].sort((a, b) =>
            (a?.customer_name || a?.name || '').localeCompare(b?.customer_name || b?.name || '')
          );
          if (isMounted) {
            setCustomers(sorted);
            // Auto-select the first customer if no customer is selected
            if (!formData.clientSelection && sorted.length > 0) {
              const firstCustomerId = sorted[0].customer_id || sorted[0].id;
              if (firstCustomerId) {
                handleFormChange('clientSelection', firstCustomerId);
              }
            } else if (formData.clientSelection) {
              const stillExists = sorted.some(c => (c.customer_id || c.id) === formData.clientSelection);
              if (!stillExists && sorted.length > 0) {
                // If selected customer no longer exists, select the first one
                const firstCustomerId = sorted[0].customer_id || sorted[0].id;
                handleFormChange('clientSelection', firstCustomerId || '');
              }
            }
          }
        } else {
          if (isMounted) {
            setCustomersError(result.error || 'Failed to load customers');
          }
        }
      } catch (error) {
        if (isMounted) {
          setCustomersError(error?.message || 'Unable to load customers. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsCustomersLoading(false);
        }
      }
    };

    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, []);

  const siteOptions = useMemo(
    () =>
      [...sites]
        .sort((a, b) => {
          const nameA = resolveSiteLabel(a).toLowerCase();
          const nameB = resolveSiteLabel(b).toLowerCase();
          return nameA.localeCompare(nameB);
        })
        .map((site) => ({
          value: resolveSiteValue(site),
          label: resolveSiteLabel(site),
        }))
        .filter((option) => option.value),
    [sites]
  );

  const findSiteLabel = (value) =>
    siteOptions.find((option) => option.value === value)?.label || '';

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    // Validate all required fields (except email and notes)
    if (!formData.siteSelection) {
      setSubmitFeedback({ success: '', error: 'Please select a site.' });
      return;
    }

    if (!formData.clientSelection) {
      setSubmitFeedback({ success: '', error: 'Please select a customer.' });
      return;
    }

    if (!formData.date) {
      setSubmitFeedback({ success: '', error: 'Please select an invoice date.' });
      return;
    }

    if (!formData.paymentMode) {
      setSubmitFeedback({ success: '', error: 'Please select a payment mode.' });
      return;
    }

    if (!formData.estimatedShipDate) {
      setSubmitFeedback({ success: '', error: 'Please select an estimated ship date.' });
      return;
    }

    if (!formData.dateOfExpiry) {
      setSubmitFeedback({ success: '', error: 'Please select a date of expiry.' });
      return;
    }

    if (!formData.billingAddress) {
      setSubmitFeedback({ success: '', error: 'Please select a billing address.' });
      return;
    }

    if (!formData.shippingAddress) {
      setSubmitFeedback({ success: '', error: 'Please enter a shipping address.' });
      return;
    }

    if (!formData.pocName) {
      setSubmitFeedback({ success: '', error: 'Please enter POC name.' });
      return;
    }

    if (!formData.pocRole) {
      setSubmitFeedback({ success: '', error: 'Please enter POC role.' });
      return;
    }

    if (!formData.pocPhone) {
      setSubmitFeedback({ success: '', error: 'Please enter POC phone number.' });
      return;
    }

    if (!formData.startTime) {
      setSubmitFeedback({ success: '', error: 'Please enter start time.' });
      return;
    }

    if (!formData.endTime) {
      setSubmitFeedback({ success: '', error: 'Please enter end time.' });
      return;
    }

    if (!formData.purposeOfBooking) {
      setSubmitFeedback({ success: '', error: 'Please enter purpose of booking.' });
      return;
    }

    if (!formData.numberOfPeople) {
      setSubmitFeedback({ success: '', error: 'Please enter number of people.' });
      return;
    }

    if (lineItems.length === 0 || lineItems.some(item => !item.name || !item.units || !item.rate)) {
      setSubmitFeedback({ success: '', error: 'Please add at least one valid line item with name, days, and rate' });
      return;
    }

    setIsSubmitting(true);
    setSubmitFeedback({ success: '', error: '' });

    try {
      const selectedCustomer = customers.find(
        c => {
          const cId = c.customer_id || c.id;
          return cId === formData.clientSelection;
        }
      );

      if (!selectedCustomer) {
        throw new Error(`Selected customer not found in the list. Please select a different customer.`);
      }

      const customerId = selectedCustomer.customer_id || selectedCustomer.id;
      
      if (!customerId) {
        throw new Error('Customer ID is missing. Please select a valid customer.');
      }

      // If we have draft booking data (no bookingId yet), create bookings now so they appear on the calendar when invoice is created
      let createdBookingIds = [];
      const existingBookingId = bookingData?.bookingId || effectiveBookingData?.bookingId;
      const draftBookingData = effectiveBookingData?.dates?.length && !existingBookingId ? effectiveBookingData : null;

      if (draftBookingData) {
        const sortedDates = [...draftBookingData.dates].sort();
        const toTimeISO = (dateStr) => ({
          start: `${dateStr}T${draftBookingData.startTime || '09:00'}:00`,
          end: `${dateStr}T${draftBookingData.endTime || '18:00'}:00`,
        });
        for (const dateStr of sortedDates) {
          const { start: startTimeISO, end: endTimeISO } = toTimeISO(dateStr);
          const apiPayload = {
            site_id: draftBookingData.siteId || undefined,
            facility_type: draftBookingData.facilityType || '',
            purpose_of_booking: draftBookingData.purposeOfBooking || '',
            dates: [dateStr],
            start_date: dateStr,
            end_date: dateStr,
            start_time: startTimeISO,
            end_time: endTimeISO,
            resident_name: draftBookingData.residentName || '',
            whos_booking: draftBookingData.whosBooking || '',
            block_number: draftBookingData.blockNumber || '',
            apartment_flat_no: draftBookingData.apartmentFlatNo || '',
            contact_number: draftBookingData.contactNumber || '',
            email: draftBookingData.email || '',
            number_of_people: draftBookingData.numberOfPeople || undefined,
            notes: draftBookingData.notes || undefined,
          };
          try {
            const result = await facilityBookingApi.createBooking(apiPayload);
            const id = result?.booking_id || result?.id || result?.data?.booking_id;
            if (id) createdBookingIds.push(id);
            
            // Log successful API call
            loggingService.logInfo(
              'amenityCreateBooking',
              'Successfully created facility booking',
              {
                endpoint: '/facility/bookings',
                method: 'POST',
                bookingId: id,
                siteId: apiPayload.site_id,
                facilityType: apiPayload.facility_type,
                date: dateStr,
                startTime: apiPayload.start_time,
                endTime: apiPayload.end_time,
              }
            ).catch(logError => {
              console.warn('[FacilityBookingInvoice] Failed to log event:', logError);
            });
          } catch (err) {
            console.error('Error creating booking for date:', dateStr, err);
            if (err.message?.includes('409')) {
              setSubmitFeedback({ success: '', error: 'Booking conflict: This facility is already booked for one or more of the selected dates/times. Please choose different dates or go back and change your selection.' });
              setIsSubmitting(false);
              return;
            }
            if (err.message?.includes('400')) {
              setSubmitFeedback({ success: '', error: 'Invalid booking data. Please go back and check all required fields.' });
              setIsSubmitting(false);
              return;
            }
            setSubmitFeedback({ success: '', error: err.message || 'Failed to create booking. Please try again.' });
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Use created or existing booking ID for bill data key
      const primaryBookingId = existingBookingId || createdBookingIds[0] || null;

      const totalLineSubtotal = summary.subtotal;
      const mappedLines = lineItems.map((item, index) => {
        const units = parseFloat(item.units) || 0;
        const unitPrice = parseFloat(item.rate) || 0;
        const lineSubtotal = units * unitPrice;
        
        let taxRate = 0;
        if (totalLineSubtotal > 0 && summary.totalTax > 0) {
          const applicableTaxes = summary.taxes.filter(t => 
            t.applyOn === 'subtotalAfterDiscount' || t.applyOn === 'subtotal'
          );
          const applicableTaxAmount = applicableTaxes.reduce((sum, t) => sum + t.amount, 0);
          if (applicableTaxAmount > 0 && summary.subtotalAfterDiscount > 0) {
            const lineProportion = lineSubtotal / totalLineSubtotal;
            const lineTaxAmount = applicableTaxAmount * lineProportion;
            taxRate = lineTaxAmount > 0 && lineSubtotal > 0 
              ? (lineTaxAmount / lineSubtotal) * 100 
              : 0;
          }
        }

        return {
          line_id: String(index + 1).padStart(3, '0'),
          item_id: `item-${index + 1}`,
          description: item.name || '',
          unit: 'days',
          quantity: units,
          unit_price: unitPrice,
          tax_rate: taxRate > 0 ? parseFloat(taxRate.toFixed(2)) : undefined,
          notes: '',
        };
      });

      const invoiceDate = formData.date || getTodayDate();
      const dueDateObj = new Date(invoiceDate);
      dueDateObj.setDate(dueDateObj.getDate() + 30);
      const dueDate = formData.dateOfExpiry || dueDateObj.toISOString().split('T')[0];

      // Billing address is the site address
      const billingSite = sites.find(s => resolveSiteValue(s) === formData.billingAddress);
      
      const billingAddress = billingSite ? {
        street: billingSite.site_name || billingSite.name || '',
        city: billingSite.city || '',
        state: billingSite.state || '',
        pincode: billingSite.pincode || billingSite.postal_code || '',
        country: billingSite.country || 'India',
      } : undefined;

      // Shipping address is the customer address (from booking - block/unit number)
      const shippingAddress = formData.shippingAddress ? {
        street: formData.shippingAddress,
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      } : undefined;

      // site_id required for invoice to appear in Fdoc / backend
      const siteId = formData.siteSelection
        ? String(formData.siteSelection).trim()
        : (bookingData?.siteId || effectiveBookingData?.siteId) || undefined;
      const apiPayload = {
        customer_id: customerId,
        ...(siteId && { site_id: siteId }),
        invoice_date: invoiceDate,
        due_date: dueDate,
        status: 'sent',
        payment_status: 'unpaid',
        currency: 'INR',
        lines: mappedLines,
        shipping_total: parseFloat(discount.shippingHandling) || 0,
        notes: [
          formData.specialNotes,
          formData.paymentMode ? `Payment Mode: ${formData.paymentMode}` : '',
          discount.insurance ? `Insurance: ₹${discount.insurance}` : '',
          formData.billNumber ? `Bill Reference: ${formData.billNumber}` : '',
        ].filter(Boolean).join('\n') || undefined,
        billing_address: billingAddress,
        shipping_address: shippingAddress,
      };

      const result = await invoiceApi.createInvoice(apiPayload);

      if (result.success) {
        // Store rate and tax rates by booking ID or booking signature before clearing cache
        // This allows the bill to retrieve these values later
        const currentBooking = bookingData || effectiveBookingData;
        
        // Create a unique key from booking details if bookingId is not available
        const getBookingKey = () => {
          if (primaryBookingId) {
            return `facilityBookingBillData_${primaryBookingId}`;
          }
          // Use booking signature: siteId_facilityType_startDate_endDate_residentName
          if (currentBooking) {
            const signature = [
              currentBooking.siteId || '',
              currentBooking.facilityType || '',
              currentBooking.startDate || '',
              currentBooking.endDate || '',
              currentBooking.residentName || '',
            ].join('_');
            return `facilityBookingBillData_${signature}`;
          }
          return null;
        };
        
        const billDataKey = getBookingKey();
        if (billDataKey && lineItems.length > 0 && lineItems[0].rate) {
          try {
            const cgstRow = taxRows.find(row => row.label === 'CGST');
            const sgstRow = taxRows.find(row => row.label === 'SGST');
            
            const billData = {
              rate: lineItems[0].rate,
              cgstRate: cgstRow?.percentage || '',
              sgstRate: sgstRow?.percentage || '',
              timestamp: Date.now(),
              bookingId: primaryBookingId || null, // Store bookingId if available
            };
            
            localStorage.setItem(billDataKey, JSON.stringify(billData));
            console.log('💾 Stored bill data with key:', billDataKey, billData);
          } catch (error) {
            console.error('Error storing bill data:', error);
          }
        }
        
        // Clear cache on successful invoice creation
        clearCache();
        
        setSubmitFeedback({ 
          success: `Invoice created successfully! Invoice Number: ${result.invoice?.invoice_number || 'N/A'}`,
          error: '' 
        });
        
        const invoicePayload = {
          invoiceNumber: result.invoice?.invoice_number || `INV-${Date.now().toString().slice(-6)}`,
          generatedAt: new Date().toISOString(),
          formData: { ...formData },
          lineItems: lineItems.map((item, index) => ({
            ...item,
            lineNumber: index + 1,
            amount: item.amount || (parseFloat(item.units) * parseFloat(item.rate)).toFixed(2),
          })),
          discount: { ...discount },
          summary: {
            ...summary,
            taxes: summary.taxes.map((row) => ({ ...row })),
          },
          taxRows: taxRows.map((row) => ({ ...row })),
          labels: {
            site: findSiteLabel(formData.siteSelection),
            billingAddress: findSiteLabel(formData.billingAddress),
            shippingAddress: formData.shippingAddress || '',
          },
          apiInvoice: result.invoice,
          apiLines: result.lines,
          isAmenityBooking: true, // Flag to identify amenity booking invoices
        };
        
        openOverlay('invoicePrint', { invoice: invoicePayload });
        
        // Navigate back to Facility Booking calendar after a short delay
        // The invoice will appear on the calendar after the refresh
        setTimeout(() => {
          navigate('/managing-facility-booking', {
            state: {
              refresh: true,
              invoiceCreated: true,
              invoiceId: result.invoice?.invoice_id,
              invoiceNumber: result.invoice?.invoice_number,
            }
          });
        }, 1500);
      } else {
        if (result.error && result.error.includes('Customer not found')) {
          throw new Error(
            `Customer not found (404). The selected customer "${selectedCustomer.customer_name || selectedCustomer.name}" (ID: ${customerId}) ` +
            `does not exist in the invoice system.`
          );
        }
        throw new Error(result.error || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setSubmitFeedback({ 
        success: '', 
        error: error.message || 'Failed to create invoice. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <form
        onSubmit={handleCreateInvoice}
        className="mx-auto flex max-w-7xl flex-col gap-8"
      >
        <header className="space-y-2">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">Create Amenity Booking Invoice</h1>
              <p className="text-gray-500">
                Capture billing and taxation details for amenity booking.
              </p>
            </div>
          </div>
        </header>

        <section className="grid gap-2 xl:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">Invoice Raised For</p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Site Selection
                </label>
                <select
                  value={formData.siteSelection}
                  onChange={(e) => handleFormChange('siteSelection', e.target.value)}
                  className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isSitesLoading}
                  required
                >
                  <option value="" disabled hidden></option>
                  {siteOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                )}
                {!isSitesLoading && !siteOptions.length && !sitesError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No sites available. Please create a site first.
                  </p>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleFormChange('date', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Payment Mode
                  </label>
                  <select
                    value={formData.paymentMode}
                    onChange={(e) => handleFormChange('paymentMode', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    required
                  >
                    <option value="" disabled>
                      Select mode
                    </option>
                    <option value="bank-transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Est. Ship Date (Start Date)
                  </label>
                  <input
                    type="date"
                    value={formData.estimatedShipDate}
                    onChange={(e) =>
                      handleFormChange('estimatedShipDate', e.target.value)
                    }
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Date of Expiry (End Date)
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfExpiry}
                    onChange={(e) => handleFormChange('dateOfExpiry', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    required
                  />
                </div>
              </div>
              
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">
              Billing Address
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Billing Address (Site)
                </label>
                <select
                  value={formData.billingAddress}
                  onChange={(e) => handleFormChange('billingAddress', e.target.value)}
                  className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isSitesLoading}
                  required
                >
                  <option value="" disabled hidden></option>
                  {siteOptions.map((option) => (
                    <option key={`billing-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                )}
                {!isSitesLoading && !siteOptions.length && !sitesError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No sites available. Please create a site first.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Shipping Address (Customer Address)
                </label>
                <Input
                  value={formData.shippingAddress}
                  onChange={(e) => handleFormChange('shippingAddress', e.target.value)}
                  placeholder="Enter customer's address (Block/Unit number)"
                  className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  required
                />
              </div>
            </div>
          </div>
        </section>

        {/* POC for Delivery Section */}
        <section>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">POC for Delivery</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">Name</label>
                <Input
                  value={formData.pocName}
                  onChange={(e) => handleFormChange('pocName', e.target.value)}
                  placeholder="Enter name"
                  className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Role</label>
                <Input
                  value={formData.pocRole}
                  onChange={(e) => handleFormChange('pocRole', e.target.value)}
                  placeholder="Enter role"
                  className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Mobile No.</label>
                <Input
                  value={formData.pocPhone}
                  onChange={(e) => handleFormChange('pocPhone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Email</label>
                <Input
                  value={formData.pocEmail}
                  onChange={(e) => handleFormChange('pocEmail', e.target.value)}
                  placeholder="name@company.com"
                  className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Booking Details Section */}
        <section>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">Booking Details</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <label className="text-xs font-semibold text-gray-500">Start Time</label>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleFormChange('startTime', e.target.value)}
                  className="mt-1 h-12 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">End Time</label>
                <Input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleFormChange('endTime', e.target.value)}
                  className="mt-1 h-12 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Purpose of Booking</label>
                <Input
                  value={formData.purposeOfBooking}
                  onChange={(e) => handleFormChange('purposeOfBooking', e.target.value)}
                  placeholder="Enter purpose of booking"
                  className="mt-1 h-12 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Number of People</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.numberOfPeople}
                  onChange={(e) => handleFormChange('numberOfPeople', e.target.value)}
                  placeholder="Enter number of people"
                  className="mt-1 h-12 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Notes</label>
                <Input
                  value={formData.bookingNotes}
                  onChange={(e) => handleFormChange('bookingNotes', e.target.value)}
                  placeholder="Any additional notes..."
                  className="mt-1 h-12 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Line Items</p>
              <p className="text-sm text-gray-500">
                Manage services, quantities, units, and rates. Amount is auto-calculated.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddLineItem}
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-500 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            >
              <PlusCircle className="h-4 w-4" />
              Add Line Item
            </button>
          </div>

          <div className="mt-6">
            {!isMobileView ? (
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">S. No</th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[200px]">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        Days
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        Rate
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        Total Amount
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={`line-item-${index}`} className="border-t">
                        <td className="px-4 py-4 text-gray-700">{index + 1}</td>
                        <td className="px-4 py-4">
                          <Input
                            value={item.name}
                            onChange={(e) =>
                              handleLineItemChange(index, 'name', e.target.value)
                            }
                            placeholder="Item or service name"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={item.units}
                            onChange={(e) =>
                              handleLineItemChange(index, 'units', e.target.value)
                            }
                            placeholder="1, 2, 3..."
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) =>
                              handleLineItemChange(index, 'rate', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="number"
                            readOnly
                            value={item.amount}
                            placeholder="0.00"
                            className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                          />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(index)}
                            disabled={lineItems.length === 1}
                            className="inline-flex items-center gap-2 rounded-full border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-gray-50">
                      <td
                        colSpan={4}
                        className="px-4 py-3 text-right text-sm font-semibold"
                      >
                        Total Amount
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        ₹ {summary.subtotal.toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="space-y-4 rounded-2xl border border-gray-100 bg-gray-50 p-1">
                {lineItems.map((item, index) => (
                  <div
                    key={`line-item-card-${index}`}
                    className="rounded-2xl border border-white bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-700">
                        Item #{index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(index)}
                        disabled={lineItems.length === 1}
                        className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                        <span className="text-red-500">Remove</span>
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Name / Description
                        </label>
                        <Input
                          value={item.name}
                          onChange={(e) =>
                            handleLineItemChange(index, 'name', e.target.value)
                          }
                          placeholder="Item or service name"
                          className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Days
                        </label>
                        <Input
                          value={item.units}
                          onChange={(e) =>
                            handleLineItemChange(index, 'units', e.target.value)
                          }
                          placeholder="1, 2, 3..."
                          className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Rate
                          </label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) =>
                              handleLineItemChange(index, 'rate', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Amount
                          </label>
                          <Input
                            type="number"
                            readOnly
                            value={item.amount}
                            placeholder="0.00"
                            className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  Total Amount: ₹ {summary.subtotal.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="flex-1 space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">Discount & Charges</p>
            <div className="mt-4 space-y-5">
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Discount Type
                  </label>
                  <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
                    {['percentage', 'amount'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleDiscountChange('type', type)}
                        className={`rounded-full px-4 py-1 text-xs font-semibold capitalize transition ${
                          discount.type === type
                            ? 'bg-green-600 text-white'
                            : 'text-gray-600'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    {discount.type === 'percentage' ? 'Enter %' : 'Enter Amount'}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount.value}
                    onChange={(e) => handleDiscountChange('value', e.target.value)}
                    placeholder="0"
                    className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Shipping / Handling
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount.shippingHandling}
                    onChange={(e) =>
                      handleDiscountChange('shippingHandling', e.target.value)
                    }
                    placeholder="0.00"
                    className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Insurance
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount.insurance}
                    onChange={(e) => handleDiscountChange('insurance', e.target.value)}
                    placeholder="0.00"
                    className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
              </div>
            </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Tax Details</p>
                <p className="text-sm text-gray-500">
                  Select applicable tax, choose base value, enter percentage. Amount gets
                  auto-calculated.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddTaxRow}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-500 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                <PlusCircle className="h-4 w-4" />
                Add Tax
              </button>
            </div>

            <div className="mt-6 space-y-4">
              {summary.taxes.map((row, index) => (
                <div
                  key={row.id}
                  className="grid gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 md:grid-cols-[1.5fr_1fr_1fr_1fr_auto]"
                >
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Tax Label
                    </label>
                    <select
                      value={row.label}
                      onChange={(e) => handleTaxRowChange(row.id, 'label', e.target.value)}
                      className="mt-1 h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    >
                      <option value="" disabled hidden></option>
                      <option value="CGST">CGST</option>
                      <option value="IGST">IGST</option>
                      <option value="SGST">SGST</option>
                      <option value="GST (CGST & IGST)">GST (CGST & IGST)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      On Which Amount
                    </label>
                    <select
                      value={row.applyOn}
                      onChange={(e) => handleTaxRowChange(row.id, 'applyOn', e.target.value)}
                      className="mt-1 h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    >
                      <option value="subtotalAfterDiscount">Subtotal (After Discount)</option>
                      <option value="shippingHandling">Shipping / Handling</option>
                      <option value="insurance">Insurance</option>
                      <option value="netTotalBeforeTax">Net total (before tax)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Enter Value %
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.percentage}
                      onChange={(e) =>
                        handleTaxRowChange(row.id, 'percentage', e.target.value)
                      }
                      placeholder="18"
                      className="mt-1 h-11 rounded-2xl border-gray-200 bg-white px-4 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Amount (auto)
                    </label>
                    <Input
                      readOnly
                      value={row.amount.toFixed(2)}
                      className="mt-1 h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                    />
                  </div>
                  <div className="flex items-end justify-end">
                    <button
                      type="button"
                      onClick={() => handleRemoveTaxRow(row.id)}
                      disabled={index === 0 && summary.taxes.length === 1}
                      className="inline-flex items-center gap-2 rounded-full border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
            </div>
          </div>

          <div className="w-full lg:w-[320px]">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">Summary</p>
              <dl className="mt-4 space-y-3 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <dt>Subtotal</dt>
                  <dd>₹ {summary.subtotal.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Discount</dt>
                  <dd>- ₹ {summary.computedDiscount.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between font-semibold text-gray-900">
                  <dt>Subtotal (After Discount)</dt>
                  <dd>₹ {summary.subtotalAfterDiscount.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Shipping / Handling</dt>
                  <dd>₹ {summary.shippingHandling.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Insurance</dt>
                  <dd>₹ {summary.insurance.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between font-semibold text-gray-900">
                  <dt>Net Total (Before Tax)</dt>
                  <dd>₹ {summary.netTotalBeforeTax.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Total Tax Amount</dt>
                  <dd>₹ {summary.totalTax.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between text-base font-bold text-green-700">
                  <dt>Grand Total</dt>
                  <dd>₹ {summary.grandTotal.toFixed(2)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Grand Total Payable
            </p>
            <p className="text-3xl font-bold text-gray-900">
              ₹ {summary.grandTotal.toFixed(2)}
            </p>
            {submitFeedback.success && (
              <p className="mt-2 text-sm text-green-600">{submitFeedback.success}</p>
            )}
            {submitFeedback.error && (
              <p className="mt-2 text-sm text-red-600">{submitFeedback.error}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-2xl bg-green-600 px-8 py-3 text-base font-semibold text-white shadow-lg transition hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </form>
  );
};

export default FacilityBookingInvoice;
